import { useCallback, useMemo } from 'react'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { Lock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  formatDailyNoteTitle,
  formatWeeklyNoteTitle,
  getCurrentWeekString,
  getTodayDateString,
  isThisWeek,
  isToday,
  parseDateString,
} from '../utils/noteDateUtils'
import { NoteRowOptionsMenu } from './NoteRowOptionsMenu'
import { NotesCalendar } from './NotesCalendar'
import { usePersonalNoteContents, usePersonalNotes } from '@/lib/db/rxdb/hooks'
import { extractPlainTextFromTiptap } from '@/routes/features/tasks/utils/editorUtils'
import { useTodayTasks } from '@/routes/features/tasks/hooks/useTodayTasks'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import useLoginStore from '@/store/LoginStore'
import { useTimezone } from '@/store/UserPreferencesStore'
import { cn } from '@/lib/utils'
import { useLocale } from '@/lib/i18n/LocaleProvider'

interface NotesListPanelProps {
  noteType: 'daily' | 'weekly'
  onNavigate?: () => void
}

type Entry = {
  date: string
  noteId?: string
  title: string
  preview: string
  isCurrent: boolean
  url: string
  /** Group heading: the month (daily) or year (weekly). */
  group: string
}

/**
 * The notes pane (design 10a): who can see these, Daily / Weekly, a calendar
 * for daily notes, then recent notes grouped by month with their first line.
 */
export function NotesListPanel({ noteType, onNavigate }: NotesListPanelProps) {
  const { t } = useTranslation('notes')
  const { locale } = useLocale()
  const { date: formatDate } = useDateTimeFormatters()
  const workspaceSlug = useLoginStore((state) => state.workspaceSlug)
  const timezone = useTimezone()
  const { notes } = usePersonalNotes(noteType)
  const { contents } = usePersonalNoteContents()
  const { tasks: dueTasks } = useTodayTasks()

  const currentPathname = useRouterState({ select: (state) => state.location.pathname })
  const navigate = useNavigate()
  const today = getTodayDateString(timezone)

  const previewByNoteId = useMemo(() => {
    const map = new Map<string, string>()
    for (const c of contents) {
      const text = (c.plainText ?? extractPlainTextFromTiptap(c.content)).trim()
      const firstLine = text.split('\n').find((line) => line.trim().length > 0) ?? ''
      map.set(c.personalNoteId, firstLine)
    }
    return map
  }, [contents])

  const entries = useMemo<Array<Entry>>(() => {
    const currentDate = noteType === 'daily' ? today : getCurrentWeekString(timezone)
    const notesByDate = new Map(notes.map((n) => [n.noteDate, n]))
    const allDates = new Set([currentDate, ...notesByDate.keys()])

    return [...allDates]
      .sort((a, b) => b.localeCompare(a))
      .map((date) => {
        const noteId = notesByDate.get(date)?.id
        const parsed = noteType === 'daily' ? parseDateString(date) : null
        return {
          date,
          noteId,
          title: noteType === 'daily' ? formatDailyNoteTitle(date, locale) : formatWeeklyNoteTitle(date),
          preview: noteId ? (previewByNoteId.get(noteId) ?? '') : '',
          isCurrent: noteType === 'daily' ? isToday(date, timezone) : isThisWeek(date, timezone),
          url: `/${workspaceSlug}/notes/${noteType}/${date}`,
          group: parsed ? formatDate(parsed, { month: 'long', year: 'numeric' }) : date.slice(0, 4),
        }
      })
  }, [noteType, today, timezone, notes, locale, previewByNoteId, workspaceSlug, formatDate])

  const groups = useMemo(() => {
    const out: Array<{ label: string; entries: Array<Entry> }> = []
    for (const entry of entries) {
      const last = out.at(-1)
      if (last && last.label === entry.group) last.entries.push(entry)
      else out.push({ label: entry.group, entries: [entry] })
    }
    return out
  }, [entries])

  const noteDates = useMemo(() => new Set(notes.map((n) => n.noteDate)), [notes])
  const selectedDate = currentPathname.match(/\/notes\/daily\/(\d{4}-\d{2}-\d{2})/)?.[1] ?? today

  const dailyUrl = `/${workspaceSlug}/notes/daily/${today}`
  const weeklyUrl = `/${workspaceSlug}/notes/weekly/${getCurrentWeekString(timezone)}`
  const currentUrl = noteType === 'daily' ? dailyUrl : weeklyUrl

  const handleDeleted = useCallback(
    (isActive: boolean) => {
      if (isActive) void navigate({ to: currentUrl })
    },
    [navigate, currentUrl],
  )

  return (
    <div className="flex h-full flex-col bg-chrome">
      <div className="px-3 pb-3 pt-4">
        <div className="mb-3 flex items-center justify-between px-1">
          <h2 className="text-[15px] font-semibold">{t('notes')}</h2>
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Lock className="size-3" />
            {t('onlyYou')}
          </span>
        </div>
        <div role="tablist" className="mb-3 grid grid-cols-2 gap-0.5 rounded-lg bg-secondary p-0.5">
          {(['daily', 'weekly'] as const).map((type) => (
            <Link
              key={type}
              role="tab"
              aria-selected={noteType === type}
              to={type === 'daily' ? dailyUrl : weeklyUrl}
              className={cn(
                'rounded-md py-1 text-center text-[13px] font-medium transition-colors',
                noteType === type ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t(type)}
            </Link>
          ))}
        </div>
        {noteType === 'daily' && (
          <NotesCalendar
            selectedDate={selectedDate}
            today={today}
            noteDates={noteDates}
            onSelectDate={(date) => {
              onNavigate?.()
              void navigate({ to: `/${workspaceSlug}/notes/daily/${date}` })
            }}
          />
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-3 pb-4">
        {groups.map((group) => (
          <section key={group.label} className="mb-2">
            <h3 className="px-2 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              {group.label}
            </h3>
            {group.entries.map((entry) => {
              const isActive = currentPathname.includes(entry.date)
              const subtitle =
                entry.isCurrent && noteType === 'daily' && dueTasks.length > 0
                  ? t('dueTodayCount', { count: dueTasks.length })
                  : entry.preview
              return (
                <div key={entry.date} className="group relative">
                  <Link
                    to={entry.url}
                    onClick={onNavigate}
                    className={cn(
                      'block rounded-lg px-3 py-2 pr-9 transition-colors',
                      isActive ? 'bg-card shadow-[0_0_0_1px_var(--border)]' : 'hover:bg-secondary/60',
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <span className="truncate text-[13px] font-semibold">{entry.title}</span>
                      {entry.isCurrent && (
                        <span className="shrink-0 rounded bg-brand-tint px-1.5 text-[10.5px] font-medium text-brand-tint-foreground">
                          {noteType === 'daily' ? t('today') : t('thisWeek')}
                        </span>
                      )}
                    </span>
                    {subtitle && (
                      <span className="mt-0.5 block truncate text-xs text-muted-foreground">{subtitle}</span>
                    )}
                  </Link>
                  {entry.noteId && (
                    <div className="absolute right-1.5 top-2">
                      <NoteRowOptionsMenu
                        noteId={entry.noteId}
                        noteTitle={entry.title}
                        onDeleted={() => handleDeleted(isActive)}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </section>
        ))}
      </div>
    </div>
  )
}
